import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  FormControl,
  makeStyles,
  MenuItem,
  Select, useMediaQuery, useTheme
} from '@material-ui/core';
import ChoicePills from '../../../components/Buttons/ChoicePills';
import { focusEditor, getQuillStoredState } from '../../../components/TextEditors/Utilities/CoreUtils';
import InputLabel from '@material-ui/core/InputLabel';
import { useHotkeys } from 'react-hotkeys-hook';
import QuillEditor2 from '../../../components/TextEditors/QuillEditor2';
import _ from 'lodash';
import { registerListener } from '../../../utils/MessageBusUtils';

const useStyles = makeStyles(
  theme => {
    return {
      sideBySide: {
        display: 'flex',
      },
      overTop: {
        display: 'flex',
        paddingBottom: '3px',
      },
      certainty: {
        [theme.breakpoints.down('sm')]: {
          width: '15rem'
        }
      },
      certaintyGroup: {
        display: "flex",
        flexDirection: "row"
      },
      certaintyLabel: {
        marginBottom: theme.spacing(2),
        textTransform: "capitalize"
      },
      certaintyValue: {
        borderRadius: 6,
        backgroundColor: theme.palette.background.wizard,
        paddingLeft: theme.spacing(1),
        margin: theme.spacing(0, 2, 2, 0),
        '& .MuiRadio-root': {
          color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
        },
        '& .MuiRadio-colorSecondary.Mui-checked': {
          color: theme.palette.type === 'dark' ? '#ffffff' : undefined,
        }
      },
      certaintyValueLabel: {
        fontWeight: "bold"
      },
      divider: {
        margin: theme.spacing(2, 0)
      },
    };
  },
  { name: "VoteAdd" }
);

function AddInitialVote(props) {
  const {
    marketId,
    onUpload,
    onChange,
    newQuantity,
    onEditorChange,
    editorName,
    defaultReason,
    isInbox=false
  } = props;
  const intl = useIntl();
  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const myEditor = <QuillEditor2
  id={editorName}
  marketId={marketId}
  mentionsAllowed
  mentionDenotationChars={['#']}
  value={getQuillStoredState(editorName) || defaultReason}
  placeholder={intl.formatMessage({ id: "yourReason" })}
/>;
  const [editor, setEditor] = useState(newQuantity > 0 ? myEditor : null);
  const classes = useStyles();
  const certainties = [5, 25, 50, 75, 100];
  // Tracks whether the most recent radio change came from an arrow key, so we
  // don't steal focus to the editor and break keyboard navigation through the
  // RadioGroup.
  const arrowKeyChangeRef = useRef(false);
  function myOnChange(event) {
    if (_.isEmpty(editor)) {
      // Have to do it like this so that they cannot type before choosing a certainty
      setEditor(myEditor);
    }
    onChange(event);
    if (!arrowKeyChangeRef.current) {
      focusEditor(editorName);
    }
  }
  function simulateCertainty(key) {
    return () => {
      const target = {value: `${key}`};
      myOnChange({target});
    };
  }

  useEffect(() => {
    // Apparently uploadFormData (which onEditorChange is backed by) is mutating
    registerListener(`editor-${editorName}`, `${editorName}-controller`, (message) => {
      const { type, newUploads, contents } = message.payload;
      switch (type) {
        case 'uploads':
          if (onUpload) {
            return onUpload(newUploads);
          }
          break;
        case 'change':
          if (onEditorChange) {
            return onEditorChange(contents);
          }
          break;
        default:
        // do nothing;
      }
    });
    return () => {};
  }, [editorName, onEditorChange, onUpload]);

  useEffect(() => {
    if (editor && !arrowKeyChangeRef.current) {
      // Run once when the editor first created, but after it is rendered.
      // Skip when the editor was created via keyboard arrow navigation so we
      // don't steal focus out of the RadioGroup.
      focusEditor(editorName);
    }
    return () => {};
  }, [editor, editorName]);

  useHotkeys('ctrl+alt+1', simulateCertainty(5), {enableOnContentEditable: true}, []);
  useHotkeys('ctrl+alt+2', simulateCertainty(25), {enableOnContentEditable: true}, []);
  useHotkeys('ctrl+alt+3', simulateCertainty(50), {enableOnContentEditable: true}, []);
  useHotkeys('ctrl+alt+4', simulateCertainty(75), {enableOnContentEditable: true}, []);
  useHotkeys('ctrl+alt+5', simulateCertainty(100), {enableOnContentEditable: true}, []);
  return (
    <div style={{paddingBottom: '0.5rem'}}>
        <FormControl className={classes.certainty}>
          {mobileLayout && (
            <>
              <InputLabel id="select-label"><FormattedMessage id='noQuantity' /></InputLabel>
              <Select
                value={newQuantity || 0}
                onChange={myOnChange}
                label={<FormattedMessage id='noQuantity' />}
              >
                {certainties.map(certainty => {
                  return ( <MenuItem
                    key={certainty}
                    value={certainty}
                  >
                    {<FormattedMessage id={`certainty${certainty}`} />}
                  </MenuItem> );
                })}
              </Select>
            </>
          )}
          {mobileLayout && (
            <div style={{marginBottom: '1rem'}}/>
          )}
          {!mobileLayout && (
            <ChoicePills
              ariaLabel="add-vote-certainty"
              value={`${newQuantity || 0}`}
              onChange={(value) => myOnChange({ target: { value } })}
              options={certainties.map((certainty) => ({
                value: `${certainty}`,
                id: `${certainty}`,
                label: <FormattedMessage id={`certainty${certainty}`} />,
                tooltip: intl.formatMessage({ id: `certaintyTip${certainty}` }),
              }))}
            />
          )}
        </FormControl>
        <div style={{paddingRight: isInbox && !mobileLayout ? '12rem' : undefined}}>
          {editor}
        </div>
    </div>
  );
}

AddInitialVote.propTypes = {
  marketId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onEditorChange: PropTypes.func,
  newQuantity: PropTypes.number,
  body: PropTypes.string
};

export default AddInitialVote;
